import { useMemo, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeader from '@/components/feature/PageHeader';
import ContentCard from '@/components/feature/ContentCard';
import Pagination from '@/components/base/Pagination';
import useSEO from '@/hooks/useSEO';
import { getCastAlphabetical, paginate, totalPages } from '@/lib/content';

const PAGE_SIZE = 6;

export default function CastPage() {
  useSEO({
    title: '演员表与角色资料 - 我的朋友他的妻子',
    description:
      '《我的朋友他的妻子》演员详情页：收录主演、配角与回忆线演员的角色设定、出场集数与表演思路，支持按演员姓名快速检索。',
    keywords: '我的朋友他的妻子演员表,演员详情,角色介绍,主演阵容',
  });

  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const allCast = useMemo(() => getCastAlphabetical(), []);

  const filtered = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    if (!query) return allCast;
    return allCast.filter((item) =>
      [item.title, item.subtitle, item.excerpt, item.tags.join(' ')]
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [allCast, keyword]);

  const pages = totalPages(filtered.length, PAGE_SIZE);
  const visible = paginate(filtered, page, PAGE_SIZE);

  const handleSearch = (value: string) => {
    setKeyword(value);
    setPage(1);
  };

  return (
    <SiteLayout>
      <PageHeader
        eyebrow="CAST & CREW"
        title="演员详情"
        description="八位演员，八种进入人物的方式。这里记录了他们饰演的角色、出场集数，以及他们在片场为角色补充的那些不会被拍到的细节。"
        crumbs={[{ label: '首页', to: '/' }, { label: '演员详情' }]}
        image={allCast[1]?.image}
      >
        <div className="w-full max-w-sm shrink-0">
          <label htmlFor="cast-search" className="mb-2 block text-xs text-foreground-500">
            按演员姓名或角色名检索
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3 py-2 focus-within:border-primary-500/60">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              id="cast-search"
              name="cast-search"
              value={keyword}
              onChange={(event) => handleSearch(event.target.value)}
              placeholder="例如：林知远、江暖"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-600 focus:outline-none"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => handleSearch('')}
                aria-label="清除搜索"
                className="text-foreground-500 transition-colors hover:text-primary-400 cursor-pointer"
              >
                <i className="ri-close-circle-line text-base"></i>
              </button>
            )}
          </div>
        </div>
      </PageHeader>

      <section className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-400">
            {keyword ? (
              <>
                “<span className="text-primary-400">{keyword}</span>” 匹配到
                <span className="text-foreground-900"> {filtered.length} </span>位演员
              </>
            ) : (
              <>
                共 <span className="text-primary-400">{filtered.length}</span> 位演员 · 按姓名索引排列
              </>
            )}
          </p>
          <span className="font-label text-xs tracking-[0.25em] text-foreground-600">
            PAGE {page} / {pages}
          </span>
        </div>

        {visible.length > 0 ? (
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {visible.map((item, index) => (
              <ContentCard key={item.id} item={item} variant="person" index={index} />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100 py-16 text-center">
            <i className="ri-user-search-line text-3xl text-foreground-600"></i>
            <p className="mt-3 text-sm text-foreground-300">没有找到匹配的演员</p>
            <p className="mt-1 text-xs text-foreground-500">换一个姓名或角色名试试，例如“陈默”</p>
            <button
              type="button"
              onClick={() => handleSearch('')}
              className="mt-4 rounded-md border border-primary-500/40 px-4 py-2 text-xs text-primary-300 transition-colors hover:bg-primary-500/10 cursor-pointer"
            >
              重置搜索
            </button>
          </div>
        )}

        <div className="mt-10">
          <Pagination page={page} total={pages} onChange={setPage} />
        </div>
      </section>
    </SiteLayout>
  );
}